// client/src/user/dashboard/chat/Sidebar.js
import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Divider,
  InputAdornment,
  IconButton,
  Avatar,
  useTheme,
  alpha,
  Stack,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  ChatBubbleOutline as ChatIcon,
  AutoAwesome as SparkleIcon,
  SupportAgent as SupportIcon,
  LogoutRounded as LogoutIcon,
  CardGiftcard as GiftIcon,
  LockReset as LockResetIcon,
  BugReport as BugReportIcon,
} from "@mui/icons-material";
import { SIDEBAR_W, LANG } from "./constants";
import ConversationItem from "./ConversationItem";
import { useHistory } from "react-router-dom";
import FreeTokens from "../../free-tokens/FreeTokens";
import ChangePassword from "../../change-password/ChangePassword";

const Sidebar = ({
  lang,
  userData,
  search,
  setSearch,
  conversations,
  groupedConvs,
  activeConv,
  handleNewChat,
  handleSelectConv,
  setDeleteDialog,
  setRenameDialog,
  onSupportClick,
}) => {
  const theme = useTheme();
  const history = useHistory();
  const [freeTokensOpen, setFreeTokensOpen] = useState(false);
  const [changePwdOpen, setChangePwdOpen] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem(process.env.REACT_APP_TOKEN + "_user");
    history.push("/user/login");
  };

  const showFreeTokensPromo =
    !userData?.daily_token_limit || userData?.daily_token_limit === 0;

  const renderConvGroup = (label, list) => {
    if (!list.length) return null;

    return (
      <React.Fragment key={label}>
        <Typography
          variant="caption"
          sx={{
            px: 2.5,
            py: 0.75,
            display: "block",
            color: "text.disabled",
            fontSize: "0.65rem",
            fontWeight: 800,
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            mt: 1.5,
          }}
        >
          {label}
        </Typography>

        {list.map((c) => (
          <ConversationItem
            key={c.id}
            conv={c}
            isActive={activeConv?.conversation_id === c.conversation_id}
            onSelect={handleSelectConv}
            onDelete={(cv) => setDeleteDialog({ open: true, conv: cv })}
            onRename={(cv) =>
              setRenameDialog({ open: true, conv: cv, value: cv.title || "" })
            }
            lang={lang}
          />
        ))}
      </React.Fragment>
    );
  };

  return (
    <Box
      sx={{
        width: { xs: "100%", sm: SIDEBAR_W },
        maxWidth: SIDEBAR_W,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
        borderRight: `1px solid ${theme.palette.divider}`,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 2,
          pt: 2.5,
          pb: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          flexShrink: 0,
          minWidth: 0,
        }}
      >
        <Avatar
          sx={{
            width: 32,
            height: 32,
            fontSize: 13,
            fontWeight: 800,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            color: theme.palette.primary.contrastText,
            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`,
            flexShrink: 0,
          }}
        >
          <SparkleIcon fontSize="small" />
        </Avatar>

        <Stack direction="column" sx={{ minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            fontWeight={800}
            color="text.primary"
            noWrap
            sx={{ lineHeight: 1.2, fontSize: "0.9rem" }}
          >
            {lang?.appName || "Free Chat Ai"}
          </Typography>
          <Typography
            variant="caption"
            color="text.disabled"
            noWrap
            sx={{ fontSize: "0.65rem" }}
          >
            {lang?.appNameDes}
          </Typography>
        </Stack>
      </Box>

      <Box
        sx={{
          px: 1.5,
          pb: 1,
          display: "flex",
          flexDirection: "column",
          gap: 0.75,
          flexShrink: 0,
        }}
      >
        <Button
          fullWidth
          onClick={handleNewChat}
          startIcon={<AddIcon sx={{ fontSize: 17 }} />}
          sx={{
            justifyContent: "flex-start",
            textTransform: "none",
            fontWeight: 700,
            fontSize: "0.8375rem",
            py: 1,
            borderRadius: 2,
            color: "primary.main",
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            border: `1.5px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            "&:hover": {
              bgcolor: alpha(theme.palette.primary.main, 0.14),
              borderColor: alpha(theme.palette.primary.main, 0.4),
            },
            transition: "all 0.15s",
          }}
        >
          {lang?.newChat || LANG.newChat}
        </Button>

        <Button
          fullWidth
          onClick={onSupportClick}
          startIcon={<SupportIcon sx={{ fontSize: 17 }} />}
          sx={{
            justifyContent: "flex-start",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.8375rem",
            py: 1,
            borderRadius: 2,
            color: "text.secondary",
            bgcolor: "transparent",
            border: `1.5px solid ${theme.palette.divider}`,
            "&:hover": {
              bgcolor: alpha(theme.palette.text.primary, 0.04),
              borderColor: alpha(theme.palette.text.primary, 0.2),
              color: "text.primary",
            },
            transition: "all 0.15s",
          }}
        >
          {lang?.support || "Support"}
        </Button>

        <Button
          fullWidth
          onClick={onSupportClick}
          startIcon={<BugReportIcon sx={{ fontSize: 17 }} />}
          sx={{
            justifyContent: "flex-start",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.8375rem",
            py: 1,
            borderRadius: 2,
            color: "text.secondary",
            bgcolor: "transparent",
            border: `1.5px solid ${theme.palette.divider}`,
            "&:hover": {
              bgcolor: alpha(theme.palette.error.main, 0.06),
              borderColor: alpha(theme.palette.error.main, 0.3),
              color: "error.main",
            },
            transition: "all 0.15s",
          }}
        >
          {lang?.reportBug || "Report Bug"}
        </Button>

        <Button
          fullWidth
          onClick={() => setFreeTokensOpen((p) => !p)}
          startIcon={<GiftIcon sx={{ fontSize: 17 }} />}
          sx={{
            justifyContent: "flex-start",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.8375rem",
            py: 1,
            borderRadius: 2,
            color: showFreeTokensPromo ? "warning.main" : "text.secondary",
            bgcolor: showFreeTokensPromo
              ? alpha(theme.palette.warning.main, 0.08)
              : "transparent",
            border: `1.5px solid ${
              showFreeTokensPromo
                ? alpha(theme.palette.warning.main, 0.3)
                : theme.palette.divider
            }`,
            "&:hover": {
              bgcolor: alpha(theme.palette.warning.main, 0.12),
              borderColor: alpha(theme.palette.warning.main, 0.4),
              color: "warning.main",
            },
            transition: "all 0.15s",
          }}
        >
          {lang?.freeTokens || "Get Free Tokens"}
          {showFreeTokensPromo && (
            <Box
              sx={{
                ml: 0.75,
                px: 0.75,
                py: 0.1,
                borderRadius: 1,
                bgcolor: "warning.main",
                color: "warning.contrastText",
                fontSize: "0.55rem",
                fontWeight: 800,
                lineHeight: 1.6,
                letterSpacing: "0.05em",
              }}
            >
              FREE
            </Box>
          )}
        </Button>
      </Box>

      <FreeTokens
        lang={lang}
        userData={userData}
        open={freeTokensOpen}
        onClose={() => setFreeTokensOpen(false)}
      />

      <ChangePassword
        open={changePwdOpen}
        onClose={() => setChangePwdOpen(false)}
        lang={lang}
      />

      <Box sx={{ px: 1.5, pb: 1, flexShrink: 0 }}>
        <TextField
          fullWidth
          size="small"
          placeholder={lang?.searchConversations || LANG.searchConversations}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 14, color: "text.disabled" }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setSearch("")}
                  sx={{ p: 0.25 }}
                >
                  <CloseIcon sx={{ fontSize: 12 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              fontSize: "0.78rem",
              bgcolor: alpha(theme.palette.text.primary, 0.04),
              "& fieldset": { borderColor: "transparent" },
              "&:hover fieldset": {
                borderColor: alpha(theme.palette.primary.main, 0.3),
              },
              "&.Mui-focused fieldset": {
                borderColor: theme.palette.primary.main,
                borderWidth: "1.5px",
              },
            },
            "& .MuiInputBase-input": {
              py: "7px",
              px: "6px",
              fontSize: "0.78rem",
            },
          }}
        />
      </Box>

      <Divider sx={{ opacity: 0.5 }} />

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          pb: 2,
          pt: 0.5,
          minHeight: 0,
          "&::-webkit-scrollbar": { width: 3 },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: alpha(theme.palette.text.primary, 0.12),
            borderRadius: 2,
          },
        }}
      >
        {conversations.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 6, px: 2 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "14px",
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 1.5,
              }}
            >
              <ChatIcon sx={{ fontSize: 22, color: "text.disabled" }} />
            </Box>

            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ fontSize: "0.75rem", display: "block", fontWeight: 500 }}
            >
              {lang?.noConversations || LANG.noConversations}
            </Typography>

            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ fontSize: "0.68rem", display: "block", mt: 0.5 }}
            >
              {lang?.startNewChat || LANG.startNewChat}
            </Typography>
          </Box>
        ) : (
          <>
            {renderConvGroup(lang?.today || LANG.today, groupedConvs.today)}
            {renderConvGroup(
              lang?.yesterday || LANG.yesterday,
              groupedConvs.yesterday,
            )}
            {renderConvGroup(lang?.older || LANG.older, groupedConvs.older)}
          </>
        )}
      </Box>

      <Divider sx={{ opacity: 0.5 }} />

      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          bgcolor: alpha(theme.palette.primary.main, 0.03),
          flexShrink: 0,
          minWidth: 0,
        }}
      >
        <Avatar
          sx={{
            width: 32,
            height: 32,
            fontSize: 13,
            fontWeight: 800,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            color: theme.palette.primary.contrastText,
            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`,
            flexShrink: 0,
          }}
        >
          {userData?.name?.[0]?.toUpperCase() || "U"}
        </Avatar>

        <Stack direction="column" sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            noWrap
            sx={{ fontSize: "0.8rem", fontWeight: 600, lineHeight: 1.2 }}
          >
            {userData?.name || "User"}
          </Typography>

          {userData?.email && (
            <Typography
              variant="caption"
              noWrap
              color="text.disabled"
              sx={{ fontSize: "0.65rem" }}
            >
              {userData.email}
            </Typography>
          )}
        </Stack>

        <Tooltip
          title={lang?.changePassword || "Change Password"}
          placement="top"
          arrow
        >
          <IconButton
            size="small"
            onClick={() => setChangePwdOpen(true)}
            sx={{
              flexShrink: 0,
              color: "text.disabled",
              borderRadius: 1.5,
              p: 0.75,
              transition: "all 0.15s",
              "&:hover": {
                color: "primary.main",
                bgcolor: alpha(theme.palette.primary.main, 0.08),
              },
            }}
          >
            <LockResetIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>

        <Tooltip title={lang?.logout || "Logout"} placement="top" arrow>
          <IconButton
            size="small"
            onClick={handleLogout}
            sx={{
              flexShrink: 0,
              color: "text.disabled",
              borderRadius: 1.5,
              p: 0.75,
              transition: "all 0.15s",
              "&:hover": {
                color: "error.main",
                bgcolor: alpha(theme.palette.error.main, 0.08),
              },
            }}
          >
            <LogoutIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default Sidebar;
